import { FC, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  styled,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { Attack, SuspectedHeadacheType } from "../../types";
import { calcDurationDisplay } from "../../helpers";

const AttackFormControl = styled(FormControl)({
  marginTop: "1rem",
});

const Spacer = styled("div")({
  flexGrow: 1,
});

interface AttackDialogProps {
  providedAttack?: Attack;
  addAttack?: (newAttack: {
    startDateTime: DateTime;
    endDateTime: DateTime;
    awake: boolean;
    disabledForTwoHours: boolean;
    suspectedHeadacheType: SuspectedHeadacheType;
    notes: string;
  }) => void;
  editAttack?: (
    attackId: number,
    newAttack: {
      startDateTime: DateTime;
      endDateTime: DateTime;
      awake: boolean;
      disabledForTwoHours: boolean;
      suspectedHeadacheType: SuspectedHeadacheType;
      notes: string;
    },
  ) => void;
  deleteAttack?: (attackId: number) => void;
  handleClose: () => void;
  open: boolean;
  variant: "add" | "edit";
}

const AttackDialog: FC<AttackDialogProps> = ({
  providedAttack,
  addAttack,
  editAttack,
  deleteAttack,
  handleClose,
  open,
  variant,
}) => {
  const [startDateTime, setStartDateTime] = useState<DateTime | null>(
    providedAttack?.startDateTime
      ? DateTime.fromISO(providedAttack.startDateTime)
      : DateTime.local(),
  );
  const [endDateTime, setEndDateTime] = useState<DateTime | null>(
    providedAttack?.endDateTime
      ? DateTime.fromISO(providedAttack.endDateTime)
      : DateTime.local(),
  );
  const [awake, setAwake] = useState(providedAttack?.awake ?? true);
  const [disabledForTwoHours, setDisabledForTwoHours] = useState(
    providedAttack?.disabledForTwoHours ?? false,
  );
  const [suspectedHeadacheType, setSuspectedHeadacheType] =
    useState<SuspectedHeadacheType>(
      providedAttack?.suspectedHeadacheType ?? "migraine",
    );
  const [notes, setNotes] = useState(providedAttack?.notes ?? "");

  const dialogTitle =
    variant === "add" ? "Add headache attack" : "Edit headache attack";
  const primaryButtonLabel = variant === "add" ? "Add" : "Update";

  const invalidDateTimes =
    startDateTime !== null &&
    endDateTime !== null &&
    endDateTime < startDateTime;

  const isInvalid =
    startDateTime === null ||
    endDateTime === null ||
    endDateTime < startDateTime;

  const handleAdd = () => {
    addAttack!({
      startDateTime: startDateTime!,
      endDateTime: endDateTime!,
      awake,
      disabledForTwoHours,
      suspectedHeadacheType,
      notes,
    });
    handleClose();
  };

  const handleDelete = () => {
    deleteAttack!(providedAttack!.attackId);
    handleClose();
  };

  const handleEdit = () => {
    editAttack!(providedAttack!.attackId, {
      startDateTime: startDateTime!,
      endDateTime: endDateTime!,
      awake,
      disabledForTwoHours,
      suspectedHeadacheType,
      notes,
    });
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <AttackFormControl>
          <Stack spacing={2}>
            {invalidDateTimes && (
              <Alert severity="error">
                End date time must not be before start date time
              </Alert>
            )}
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <Stack direction="row" spacing={2}>
                <DateTimePicker
                  label="Start date and time"
                  value={startDateTime}
                  onChange={(newStartDateTime) =>
                    setStartDateTime(newStartDateTime)
                  }
                />
                <DateTimePicker
                  label="End date and time"
                  value={endDateTime}
                  onChange={(newEndDateTime) => setEndDateTime(newEndDateTime)}
                />
              </Stack>
            </LocalizationProvider>
          </Stack>
          {startDateTime && endDateTime && (
            <FormHelperText>
              Duration:{" "}
              {calcDurationDisplay(
                startDateTime.toISO()!,
                endDateTime.toISO()!,
              )}
            </FormHelperText>
          )}
        </AttackFormControl>
        <AttackFormControl>
          <FormLabel id="radio-buttons-awake-attack-label">
            Were you awake or asleep during the onset of this headache attack?
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby="radio-buttons-awake-attack-label"
            value={awake}
            name="radio-buttons-awake-attack"
            onChange={(e) => setAwake(e.target.value === "true")}
          >
            <FormControlLabel value="true" control={<Radio />} label="Awake" />
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Asleep"
            />
          </RadioGroup>
        </AttackFormControl>
        <AttackFormControl>
          <FormLabel id="radio-buttons-disabling-attack-label">
            Were you disabled for over two hours during this headache attack?
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby="radio-buttons-disabling-attack-label"
            value={disabledForTwoHours}
            name="radio-buttons-disabling-attack"
            onChange={(e) => setDisabledForTwoHours(e.target.value === "true")}
          >
            <FormControlLabel value="true" control={<Radio />} label="Yes" />
            <FormControlLabel value="false" control={<Radio />} label="No" />
          </RadioGroup>
        </AttackFormControl>
        <AttackFormControl fullWidth>
          <InputLabel id="select-suspected-headache-type-label">
            Suspected headache type
          </InputLabel>
          <Select
            labelId="select-suspected-headache-type-label"
            id="select-suspected-headache-type"
            value={suspectedHeadacheType}
            label="Suspected headache type"
            onChange={(e) =>
              setSuspectedHeadacheType(e.target.value as SuspectedHeadacheType)
            }
          >
            <MenuItem value="migraine">Migraine</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </AttackFormControl>
        <AttackFormControl fullWidth>
          <TextField
            id="additional-notes"
            label="Additional notes (optional)"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </AttackFormControl>
      </DialogContent>

      <DialogActions>
        {variant === "edit" && (
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        )}
        <Spacer />
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={variant === "add" ? handleAdd : handleEdit}
          variant="contained"
          disableElevation
          disabled={isInvalid}
        >
          {primaryButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttackDialog;
